import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Sparkles, Calendar, BarChart2, ArrowRight, MessageSquare } from 'lucide-react';
import { useUserStore } from '../../../stores/userStore';
import LiveChatSupport from '../support/LiveChatSupport';

interface WelcomeHeroProps {
  userName: string;
}

export default function WelcomeHero({ userName }: WelcomeHeroProps) {
  const navigate = useNavigate();
  const isPro = useUserStore(state => state.isPro);
  const isAdmin = useUserStore(state => state.isAdmin);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const userGoals = useUserStore(state => state.getUserGoals());
  
  // Check if user has access to Pro support (either Pro user or Admin)
  const hasProSupport = isPro() || isAdmin();

  // Get personalized greeting based on user goals
  const getPersonalizedGreeting = () => {
    if (!userGoals) return "Bem-vindo(a) ao seu dashboard Kontty";
    
    switch (userGoals.goal) {
      case 'crescer':
        return `Vamos ajudar você a crescer no ${userGoals.platform}!`;
      case 'engajar':
        return `Pronto para aumentar seu engajamento no ${userGoals.platform}?`;
      case 'monetizar':
        return `Vamos trabalhar na monetização do seu canal ${userGoals.platform}!`;
      case 'marca':
        return `Construindo sua marca pessoal no ${userGoals.platform}!`;
      case 'vendas':
        return `Vamos impulsionar suas conversões no ${userGoals.platform}!`;
      default:
        return `Bem-vindo(a) ao seu dashboard Kontty`;
    }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl p-8 shadow-xl overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-x-16 -translate-y-32 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-x-8 translate-y-16 blur-3xl"></div>
      
      <div className="relative z-10">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center animate-float">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Olá, {userName}!</h1>
            <p className="text-white/80">{getPersonalizedGreeting()}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105 cursor-pointer" onClick={() => navigate('/dashboard/ideias')}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Ideias de Conteúdo</h3>
            </div>
            <p className="text-white/80 text-sm">Gere ideias personalizadas para suas redes sociais</p>
            <div className="flex justify-end mt-3">
              <ArrowRight className="h-5 w-5 text-white/60 animate-arrow-point" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105 cursor-pointer" onClick={() => navigate('/dashboard/calendario')}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Calendário</h3>
            </div>
            <p className="text-white/80 text-sm">Organize sua estratégia de publicação</p>
            <div className="flex justify-end mt-3">
              <ArrowRight className="h-5 w-5 text-white/60 animate-arrow-point" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105 cursor-pointer" onClick={() => navigate('/dashboard/analises')}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <BarChart2 className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Análises</h3>
            </div>
            <p className="text-white/80 text-sm">Acompanhe o desempenho do seu conteúdo</p>
            <div className="flex justify-end mt-3">
              <ArrowRight className="h-5 w-5 text-white/60 animate-arrow-point" />
            </div>
          </div>
        </div>

        {hasProSupport && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs px-2 py-1 rounded-bl-lg">
              PRO
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center animate-pulse-custom">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Suporte Pro ao Vivo</h3>
                  <p className="text-white/80 text-sm">Assistência prioritária disponível 24/7</p>
                </div>
              </div>
              <button 
                onClick={() => setShowLiveChat(true)}
                className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center"
              >
                Iniciar Chat
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Live Chat Support Modal */}
      {showLiveChat && <LiveChatSupport onClose={() => setShowLiveChat(false)} />}
    </div>
  );
}